import { useMemo, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { FoodTypeGlyph } from "../../components/FoodTypeGlyph";
import { IngredientGlyph } from "../../components/IngredientGlyph";
import { LocaleSwitcher } from "../../components/LocaleSwitcher";
import { ingredientById, selectableIngredients } from "../../data/ingredients";
import { recipeById, recipeFamilies } from "../../data/recipes";
import {
  getCandidates,
  getStarterCandidates,
  selectionFromState,
} from "../../domain/candidates";
import {
  ingredientMatchesSearch,
  normalizeIngredientSearch,
} from "../../domain/ingredientSearch";
import { localizedIngredientName } from "../../domain/localize";
import { pluralize } from "../../domain/pluralize";
import { bestRecovery } from "../../domain/recovery";
import {
  compatibleIngredientIds,
  getSuggestions,
} from "../../domain/suggestions";
import {
  COOKING_LOCATIONS,
  FOOD_TYPES,
  INGREDIENT_CATEGORIES,
  type AppState,
  type CookingLocationId,
  type FoodTypeId,
  type IngredientCategory,
  type Locale,
  type SelectionState,
  type Suggestion,
} from "../../domain/types";
import { RecoveryResults } from "../results/RecoveryResults";
import { RecipeResults } from "../results/RecipeResults";

const STARTER_CANDIDATES = getStarterCandidates();
const STARTER_RECIPE_IDS = new Set(
  STARTER_CANDIDATES.map((candidate) => candidate.recipeId),
);

interface Props {
  state: AppState;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  statusMessage: string | null;
  returnFocusRecipeId: string | null;
  onFoodType: (id: FoodTypeId) => void;
  onCookingLocation: (id: CookingLocationId) => void;
  onIngredient: (id: string) => void;
  onVeganOnly: (value: boolean) => void;
  onClear: () => void;
  onOpenRecipe: (id: string) => void;
  onShare: () => void;
  onRecover: (selection: SelectionState, recipeId?: string) => void;
  onSuggestion: (suggestion: Suggestion) => void;
}

export function Builder({
  state,
  locale,
  onLocaleChange,
  statusMessage,
  returnFocusRecipeId,
  onFoodType,
  onCookingLocation,
  onIngredient,
  onVeganOnly,
  onClear,
  onOpenRecipe,
  onShare,
  onRecover,
  onSuggestion,
}: Props) {
  const [activeCategory, setActiveCategory] =
    useState<IngredientCategory>("produce");
  const [search, setSearch] = useState("");
  const [expandedSelectionKey, setExpandedSelectionKey] = useState<
    string | null
  >(null);
  const selection = useMemo(() => selectionFromState(state), [state]);
  const selectionKey = [
    selection.foodTypeIds.join("+"),
    selection.cookingLocationIds.join("+"),
    selection.ingredientIds.join(","),
    selection.veganOnly ? "vegan" : "",
  ].join("|");
  const candidates = useMemo(() => getCandidates(selection), [selection]);
  const compatibleIngredients = useMemo(
    () => compatibleIngredientIds(selection, candidates),
    [selection, candidates],
  );
  const suggestions = useMemo(
    () => getSuggestions(selection, candidates, undefined, locale),
    [selection, candidates, locale],
  );
  const recovery = useMemo(
    () => (candidates.length === 0 ? bestRecovery(selection, locale) : null),
    [selection, candidates.length, locale],
  );
  const availableFoodTypeIds = useMemo(() => {
    if (selection.foodTypeIds.length === 0) {
      return new Set(
        candidates.flatMap((c) => {
          const r = recipeById.get(c.recipeId);
          return r ? [r.foodType] : [];
        }),
      );
    }
    return new Set(FOOD_TYPES.map((option) => option.id));
  }, [selection, candidates]);
  const availableLocationIds = useMemo(() => {
    if (selection.cookingLocationIds.length === 0) {
      return new Set(
        candidates.flatMap((c) => {
          const r = recipeById.get(c.recipeId);
          return r ? [r.primaryCookingLocation] : [];
        }),
      );
    }
    return new Set(COOKING_LOCATIONS.map((option) => option.id));
  }, [selection, candidates]);

  const query = normalizeIngredientSearch(search);
  const ingredientOptions = selectableIngredients.filter((ingredient) => {
    if (query) return ingredientMatchesSearch(ingredient, query, locale);
    return ingredient.category === activeCategory;
  });

  const hasSelection =
    state.foodTypeIds.length > 0 ||
    state.cookingLocationIds.length > 0 ||
    state.ingredientIds.length > 0 ||
    state.veganOnly;
  const displayedCandidates = useMemo(
    () =>
      hasSelection
        ? candidates
        : [
            ...STARTER_CANDIDATES,
            ...candidates.filter(
              (candidate) => !STARTER_RECIPE_IDS.has(candidate.recipeId),
            ),
          ],
    [hasSelection, candidates],
  );
  const resultLimit =
    expandedSelectionKey === selectionKey ? candidates.length : 3;
  const canClear =
    hasSelection ||
    search.length > 0 ||
    activeCategory !== "produce" ||
    expandedSelectionKey !== null;

  function clearEverything() {
    setSearch("");
    setActiveCategory("produce");
    setExpandedSelectionKey(null);
    onClear();
  }

  function navigateCategoryTabs(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    categoryId: IngredientCategory,
  ) {
    const currentIndex = INGREDIENT_CATEGORIES.findIndex(
      (category) => category.id === categoryId,
    );
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight")
      nextIndex = (currentIndex + 1) % INGREDIENT_CATEGORIES.length;
    if (event.key === "ArrowLeft")
      nextIndex =
        (currentIndex - 1 + INGREDIENT_CATEGORIES.length) %
        INGREDIENT_CATEGORIES.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = INGREDIENT_CATEGORIES.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextCategory = INGREDIENT_CATEGORIES[nextIndex];
    setActiveCategory(nextCategory.id);
    document.getElementById(`ingredient-tab-${nextCategory.id}`)?.focus();
  }

  return (
    <main>
      <header className="site-header">
        <a
          className="brand"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            clearEverything();
          }}
        >
          <span className="brand-mark" aria-hidden="true">
            F
          </span>
          <span>FlavorCraft</span>
        </a>
        <div className="header-actions">
          <LocaleSwitcher locale={locale} onChange={onLocaleChange} />
          <button className="text-button" onClick={onShare}>
            Share
          </button>
          <button
            className="text-button"
            onClick={clearEverything}
            disabled={!canClear}
          >
            Clear all
          </button>
        </div>
      </header>

      <section className="builder-hero" aria-labelledby="builder-title">
        <div>
          <p className="eyebrow">Cook from what you have</p>
          <h1 id="builder-title">Start with one thing.</h1>
          <p className="hero-copy">
            Pick a dish, a cooking spot, or an ingredient. There is no required
            first step.
          </p>
        </div>
        <div className="hero-stamp" aria-hidden="true">
          <span>{recipeFamilies.length}</span>
          <small>connected ideas</small>
        </div>
      </section>

      <div className="status-region" aria-live="polite" aria-atomic="true">
        {statusMessage ??
          (hasSelection
            ? `${candidates.length} ${pluralize(candidates.length, locale, { en: ["recipe", "recipes"] })} available`
            : `Select anything to begin. ${candidates.length} recipes available.`)}
      </div>

      {hasSelection && (
        <section className="selection-ticket" aria-label="Your current choices">
          <div className="ticket-heading">
            <span>Your counter</span>
            <strong>{candidates.length} fits</strong>
          </div>
          <div className="chip-row">
            {state.foodTypeIds.map((id) => (
              <button
                key={id}
                aria-label={`Remove ${
                  FOOD_TYPES.find((item) => item.id === id)?.label ?? id
                }`}
                className="selection-chip"
                onClick={() => onFoodType(id)}
              >
                {FOOD_TYPES.find((item) => item.id === id)?.label}{" "}
                <span aria-hidden="true">×</span>
              </button>
            ))}
            {state.cookingLocationIds.map((id) => (
              <button
                key={id}
                aria-label={`Remove ${
                  COOKING_LOCATIONS.find((item) => item.id === id)?.label ?? id
                }`}
                className="selection-chip"
                onClick={() => onCookingLocation(id)}
              >
                {COOKING_LOCATIONS.find((item) => item.id === id)?.label}{" "}
                <span aria-hidden="true">×</span>
              </button>
            ))}
            {state.ingredientIds.map((id) => {
              const ingredient = ingredientById.get(id);
              const name = ingredient
                ? localizedIngredientName(ingredient, locale)
                : id;
              return (
                <button
                  aria-label={`Remove ${name}`}
                  className="selection-chip"
                  key={id}
                  onClick={() => onIngredient(id)}
                >
                  {name} <span aria-hidden="true">×</span>
                </button>
              );
            })}
            {state.veganOnly && (
              <button
                aria-label="Remove Vegan only"
                className="selection-chip"
                onClick={() => onVeganOnly(false)}
              >
                Vegan only <span aria-hidden="true">×</span>
              </button>
            )}
          </div>
        </section>
      )}

      <div className="builder-layout">
        <div className="selectors-column">
          <section
            className="selector-section"
            aria-labelledby="food-type-title"
          >
            <div className="section-heading">
              <div>
                <span className="section-kicker">Shape</span>
                <h2 id="food-type-title">What are you making?</h2>
              </div>
              <span>Choose any</span>
            </div>
            <div className="option-grid option-grid--three option-grid--food-types">
              {FOOD_TYPES.map((option) => {
                const available = availableFoodTypeIds.has(option.id);
                return (
                  <button
                    className="choice-card"
                    key={option.id}
                    aria-pressed={state.foodTypeIds.includes(option.id)}
                    aria-disabled={!available}
                    aria-describedby={
                      !available ? "unavailable-food-type-reason" : undefined
                    }
                    onClick={() => available && onFoodType(option.id)}
                  >
                    <FoodTypeGlyph foodTypeId={option.id} />
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </button>
                );
              })}
            </div>
            <span className="sr-only" id="unavailable-food-type-reason">
              No current recipe supports this food type with your other choices.
            </span>
          </section>

          <section className="selector-section" aria-labelledby="cooking-title">
            <div className="section-heading">
              <div>
                <span className="section-kicker">Heat</span>
                <h2 id="cooking-title">Where is it cooked?</h2>
              </div>
              <span>Choose any</span>
            </div>
            <div className="option-grid option-grid--three">
              {COOKING_LOCATIONS.map((option) => {
                const available = availableLocationIds.has(option.id);
                return (
                  <button
                    className="choice-card choice-card--location"
                    key={option.id}
                    aria-pressed={state.cookingLocationIds.includes(option.id)}
                    aria-disabled={!available}
                    aria-describedby={
                      !available ? "unavailable-location-reason" : undefined
                    }
                    onClick={() => available && onCookingLocation(option.id)}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </button>
                );
              })}
            </div>
            <span className="sr-only" id="unavailable-location-reason">
              No current recipe supports this cooking location with your other
              choices.
            </span>
          </section>

          <section
            className="selector-section ingredient-section"
            aria-labelledby="ingredient-title"
          >
            <div className="section-heading section-heading--ingredients">
              <div>
                <span className="section-kicker">Mise en place</span>
                <h2 id="ingredient-title">What do you have?</h2>
              </div>
              <label className="search-box">
                <span className="sr-only">Search ingredients</span>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Find an ingredient"
                  type="search"
                />
              </label>
            </div>
            {!query && (
              <div
                className="category-tabs"
                role="tablist"
                aria-label="Ingredient categories"
              >
                {INGREDIENT_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    id={`ingredient-tab-${category.id}`}
                    role="tab"
                    aria-controls="ingredient-panel"
                    aria-selected={activeCategory === category.id}
                    tabIndex={activeCategory === category.id ? 0 : -1}
                    className={`category-tab category-tab--${category.id}`}
                    onClick={() => setActiveCategory(category.id)}
                    onKeyDown={(event) =>
                      navigateCategoryTabs(event, category.id)
                    }
                  >
                    {category.shortLabel}
                  </button>
                ))}
              </div>
            )}
            <div
              id="ingredient-panel"
              className="ingredient-grid"
              role="tabpanel"
              aria-label={
                query
                  ? "Ingredient search results"
                  : `${activeCategory} ingredients`
              }
            >
              {ingredientOptions.map((ingredient) => {
                const selected = state.ingredientIds.includes(ingredient.id);
                const available =
                  selected || compatibleIngredients.has(ingredient.id);
                const reasonId = `unavailable-${ingredient.id}`;
                return (
                  <div className="ingredient-option" key={ingredient.id}>
                    <button
                      className={`ingredient-button ingredient-button--${ingredient.category}`}
                      aria-pressed={selected}
                      aria-disabled={!available}
                      aria-describedby={!available ? reasonId : undefined}
                      onClick={() => available && onIngredient(ingredient.id)}
                    >
                      <IngredientGlyph
                        ingredientId={ingredient.id}
                        category={ingredient.category}
                      />
                      <span>{localizedIngredientName(ingredient, locale)}</span>
                      <span aria-hidden="true">{selected ? "✓" : "+"}</span>
                    </button>
                    {!available && (
                      <span className="sr-only" id={reasonId}>
                        No current recipe supports this addition.
                      </span>
                    )}
                  </div>
                );
              })}
              {ingredientOptions.length === 0 && (
                <div className="empty-search">
                  <p>No ingredients found. Try another name.</p>
                  <button className="text-button" onClick={() => setSearch("")}>
                    Clear search
                  </button>
                </div>
              )}
            </div>
            <label className="vegan-toggle">
              <input
                type="checkbox"
                checked={state.veganOnly}
                onChange={(event) => onVeganOnly(event.target.checked)}
              />
              <span>
                <strong>Vegan only</strong>
                <small>Use reviewed plant-based options in every slot</small>
              </span>
            </label>
          </section>
        </div>

        <aside className="results-column" aria-labelledby="results-title">
          {candidates.length > 0 ? (
            <>
              <section
                className="suggestion-panel"
                aria-labelledby="suggestions-title"
              >
                <div className="section-heading section-heading--compact">
                  <div>
                    <span className="section-kicker">Next move</span>
                    <h2 id="suggestions-title">Build the idea</h2>
                  </div>
                </div>
                <div className="suggestion-list">
                  {suggestions.slice(0, 4).map((suggestion) => (
                    <button
                      key={`${suggestion.kind}-${suggestion.id}`}
                      onClick={() => onSuggestion(suggestion)}
                    >
                      <span>
                        <strong>{suggestion.label}</strong>
                        <small>{suggestion.reason}</small>
                      </span>
                      <span aria-hidden="true">+</span>
                    </button>
                  ))}
                </div>
              </section>
              <section className="results-section">
                <div className="section-heading">
                  <div>
                    <span className="section-kicker">Recipe tickets</span>
                    <h2 id="results-title" tabIndex={-1}>
                      {hasSelection ? "What fits now" : "Good places to start"}
                    </h2>
                  </div>
                  <span>{candidates.length} total</span>
                </div>
                <RecipeResults
                  candidates={displayedCandidates}
                  selection={selection}
                  locale={locale}
                  onOpen={onOpenRecipe}
                  limit={hasSelection ? resultLimit : Math.max(resultLimit, 6)}
                  returnFocusRecipeId={returnFocusRecipeId}
                />
                {resultLimit < candidates.length && (
                  <button
                    className="show-more"
                    onClick={() => setExpandedSelectionKey(selectionKey)}
                  >
                    Show all {candidates.length} recipes
                  </button>
                )}
              </section>
            </>
          ) : (
            <RecoveryResults
              recovery={recovery}
              locale={locale}
              onClear={clearEverything}
              onRecover={onRecover}
              returnFocusRecipeId={returnFocusRecipeId}
            />
          )}
        </aside>
      </div>
      <footer className="site-footer">
        <span>FlavorCraft</span>
        <span>Static by design. No account, no tracking.</span>
      </footer>
    </main>
  );
}
